import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { InvestmentInstructionsVisualizationComponent } from '../investment-instructions-visualization.component';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  InvestmentInstructionUtils,
  NamedAccountInvestmentInstruction
} from '../../../../../utils/investment-instruction-utils';
import { InvestmentInstructions } from '../../../../../models/transactions/investment-instructions';
import { MemberAccount } from '../../../../../models/transactions/member-account';

@Component({
  selector: 'app-current-instructions-visualization',
  templateUrl: './current-instructions-visualization.component.html',
  styleUrls: ['./current-instructions-visualization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentInstructionsVisualizationComponent
  extends InvestmentInstructionsVisualizationComponent
  implements OnChanges
{
  @Input() instructions: InvestmentInstructions[] = [];
  @Input() accounts: MemberAccount[] | null = [];
  @Input() label: string = $localize`Current`;

  @ViewChildren('grid') gridElements!: QueryList<ElementRef>;

  private _instructions = new BehaviorSubject<InvestmentInstructions[]>([]);
  public instructions$ = this._instructions.asObservable();

  private _accounts = new BehaviorSubject<MemberAccount[]>([]);
  public accounts$ = this._accounts.asObservable();

  public distributionsAreEqual$ = this.instructions$.pipe(
    map(instructions => InvestmentInstructionUtils.distributionsAreEqual(instructions))
  );

  public instructionsForDisplay$: Observable<NamedAccountInvestmentInstruction[]> = combineLatest([
    this.instructions$,
    this.distributionsAreEqual$,
    this.accounts$
  ]).pipe(
    map(([instructions, equalDistributions, accounts]) => {
      const instructionsForDisplay = equalDistributions ? instructions.slice(0, 1) : instructions;
      return InvestmentInstructionUtils.withAccountRowDisplayNames(instructionsForDisplay, accounts);
    })
  );

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.accounts) {
      this._accounts.next(this.accounts ?? []);
    }
    if (changes.instructions) {
      this._instructions.next(this.instructions ?? []);
      // If no forced columns are set, calculate using the longest instruction length
      if (!this.forcedColumns) {
        const forcedColumns = this.instructions.reduce((max, instruction) => {
          const instructionsLength = instruction.investmentInstructions?.length;
          return Math.max(max, instructionsLength);
        }, 0);
        this._forcedColumns.next(forcedColumns);
      }
    }
  }

  public trackByAccountId(_index: number, item: NamedAccountInvestmentInstruction): number {
    return item.instruction?.accountDetails?.accountId;
  }
}
